<template>
  <v-row>
    <v-col cols="12" v-if="$store.state.payments.retrieveLoader">
      <v-skeleton-loader class="mt-5" type="article" />
    </v-col>
    <v-col cols="12" v-else>
      <v-card>
        <v-toolbar color="transparent" flat height="55">
          <v-toolbar-title class="text--secondary subtitle-1 font-weight-medium">
            <v-row align="center" justify="start" no-gutters>
              <router-link class="subtitle-1 routerLink font-weight-medium" :to="{name: 'PaymentListCreate'}">Pagos</router-link>
              <v-icon class="mx-1 pt-1" color="secondary">mdi-chevron-right</v-icon>
              <span class="subtitle-1 font-weight-medium d-inline-block text-truncate" :style="$vuetify.breakpoint.width < 1200 ? 'max-width: 300px;' : 'max-width: 500px;'">{{instance.description}}</span>
            </v-row>
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <template v-if="['failed', 'incomplete', 'requires_payment_method'].includes(instance.status)">
                <v-btn class="ml-2" v-if="instance.customer" @click="dialogAuthorization=true" outlined color="secondary"><v-icon small left>mdi-credit-card</v-icon>Cargar a una tarjeta</v-btn>
                <v-btn class="ml-2" v-if="account.country.id === 'BR' && !boletosList.length" @click="createBoleto" outlined color="secondary"><v-icon small left>mdi-invoice-text</v-icon>Generar boleto</v-btn>
                <v-btn class="ml-2" @click="dialogSource=true" outlined color="secondary"><v-icon small left>mdi-undo</v-icon>Agregar pago externo</v-btn>
              </template>
              <template v-if="!['succeeded', 'processing'].includes(instance.status) && !instance.invoice">
                <v-btn class="ml-1" @click="destroyPayment" min-width="120" outlined color="red">Eliminar pago</v-btn>
              </template>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />

        <!-- if payment failed -->
        <template v-if="instance.status === 'failed'">
          <v-card-text class="py-4">
            <v-alert class="my-0 body-2" dense prominent color="amber lighten-4" border="left" icon="mdi-information">
              <v-row align="center">
                <v-col class="grow">
                  <span class="brown--text darken-4">El cargo a la tarjeta del cliente ha fallado. {{$t(`api.${instance.failed_reason}`)}}.</span>
                </v-col>
                <v-col class="shrink">
                  <v-btn class="mr-2" :href="`${$DOCS_BASE}/docs/integrations-payment-processors#errores`" color="primary" :ripple="false" target="_blank" text>Más información<v-icon right>mdi-open-in-new</v-icon></v-btn>
                </v-col>
              </v-row>
            </v-alert>
          </v-card-text>
          <v-divider />
        </template>
        <!-- end if payment failed -->

        <v-card-text class="pa-0">
          <v-row class="mx-0 px-1 background">
            <v-col cols="2">
              <span class="d-block mb-1">Monto</span>
              <span class="body-1 font-weight-medium mr-1">{{instance.amount | currency(instance.currency)}}</span>
              <span>{{instance.currency}}</span>
            </v-col>

            <template v-if="instance.balance">
              <v-divider class="my-2" vertical />
              <v-col cols="2">
                <span class="d-block mb-1">Pendiente</span>
                <span class="body-1 font-weight-medium mr-1">{{instance.balance | currency(instance.currency)}}</span>
                <span>{{instance.currency}}</span>
              </v-col>
            </template>

            <v-divider class="my-2" vertical />
            <v-col cols="3" class="ml-2">
              <span class="d-block mb-1">Cliente</span>
              <v-btn :to="{name:'CustomerRetrieveUpdate', params:{id: instance.customer.id}}" v-if="instance.customer" height="20" text color="primary" retain-focus-on-click :ripple="false">
                <span class="d-inline-block text-truncate" style="max-width: 200px;">{{instance.customer.name}}</span>
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
              <span class="font-italic text--disabled" v-else>Cliente no especificado</span>
            </v-col>

            <v-divider class="my-2" vertical />

            <!-- status -->
            <v-col md="2">
              <span class="d-block mb-1">Estado</span>
              <v-chip v-if="instance.status === 'failed'" color="defaultGrey" text-color="secondary" small>Fallido</v-chip>
              <v-chip v-else-if="instance.status === 'requires_payment_method'" color="defaultGrey" text-color="secondary" small>Pendiente</v-chip>
              <v-chip v-else-if="instance.status === 'cancelled'" class="caption" color="defaultGrey" text-color="secondary" small>Cancelado<v-icon class="ml-1" x-small>mdi-close</v-icon></v-chip>
              <v-chip v-else-if="instance.status === 'processing'" class="caption" color="defaultGrey" text-color="secondary" small>Procesando</v-chip>
              <template v-else-if="instance.status === 'succeeded' && totalRefunded">
                <v-chip v-if="totalRefunded === instance.amount" class="caption" color="defaultGrey" text-color="secondary" small>Reembolso total<v-icon class="ml-1" x-small>mdi-reply</v-icon></v-chip>
                <v-chip v-else-if="totalRefunded < instance.amount" class="caption" color="defaultGrey" text-color="secondary" small>Reembolso parcial<v-icon class="ml-1" x-small>mdi-reply</v-icon></v-chip>
              </template>
              <v-chip v-else-if="instance.status === 'succeeded'" color="lightBlue" text-color="primary" small>Pagado<v-icon class="ml-1" x-small>mdi-check</v-icon></v-chip>
              <v-chip v-else-if="instance.status === 'incomplete'" color="defaultGrey" text-color="secondary" small>Incompleto</v-chip>
            </v-col>
            <!-- end status -->

            <v-divider class="my-2" vertical />

            <v-col class="ml-2">
              <span class="d-block mb-1">Vencimiento</span>
              <div class="body-1 font-weight-medium mt-1">{{instance.date | date}}</div>
            </v-col>
          </v-row>
          <v-divider />
          <v-row>
            <v-col cols="12" class="pb-0">
              <v-list class="transparent">
                <v-list-item>
                  <v-list-item-title class="text--secondary subtitle-1 font-weight-medium">Información general</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="6">
              <v-list class="transparent">
                <v-list-item>
                  <v-list-item-title>Descripción</v-list-item-title>
                  <v-list-item-subtitle>{{instance.description}}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>ID</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-tooltip right transition="scale-transition">
                      <template v-slot:activator="{on}">
                        <v-btn class="font-weight-regular" @click="toClipboard(instance.id)" text color="secondary" v-on="on" :ripple="false">{{instance.id}}</v-btn>
                      </template>
                      <span>Copiar</span>
                    </v-tooltip>
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Creado el</v-list-item-title>
                  <v-list-item-subtitle>{{instance.created | dateTime}}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="6">
              <v-list class="transparent" v-if="['requires_payment_method', 'failed'].includes(instance.status)">
                <v-list-item>
                  <v-list-item-title>Habilitar la página de pago</v-list-item-title>
                  <v-list-item-subtitle class="overflow-visible">
                    <v-switch class="ma-0" v-model="editable.enable_checkout" @change="updatePayment" :width="32" :height="18"  hide-details />
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item v-if="editable.enable_checkout">
                  <v-list-item-title>Página de pago</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-btn :href="`${$PAY_BASE}/payments/${instance.id}`" target="_blank" height="24" text color="primary" :ripple="false">Ir a la página de pago<v-icon right>mdi-arrow-right</v-icon></v-btn>
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- refunds -->
      <v-card class="pa-0 my-5" v-if="instance.refunds.length">
        <v-toolbar flat height="55">
          <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Reembolsos</v-toolbar-title>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0">
          <v-list class="background" three-line>
            <v-list-item v-for="d in instance.refunds" :key="d.id">
              <v-list-item-icon>
                <v-icon color="primary">mdi-reply</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>
                  <span class="body-2 text--secondary font-weight-medium">
                    {{d.amount | number}} {{instance.currency}} reembolsados por
                    <template v-if="d.reason === 'requested_by_customer'">solicitud del cliente</template>
                    <template v-else-if="d.reason === 'fraudulent'">posible fraude</template>
                    <template v-else-if="d.reason === 'duplicate'">duplicación</template>
                  </span>
                </v-list-item-title>
                <v-list-item-subtitle>
                  Identificador de referencia del gateway
                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <code class="body-2 text--secondary" v-bind="attrs" v-on="on" @click="toClipboard(d.receipt)">{{d.receipt}}<v-icon color="secondary" right small>mdi-content-copy</v-icon></code>
                    </template>
                    <span>Copiar</span>
                  </v-tooltip>
                </v-list-item-subtitle>
                <v-list-item-subtitle>
                  <span class="font-italic text--disabled">{{d.created | dateTime}}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
      </v-card>
      <!-- end refunds -->

      <!-- pay -->
      <v-card class="pa-0 my-5" v-if="['succeeded', 'incomplete'].includes(instance.status) || instance.intent">
        <v-toolbar flat height="55">
          <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Transacciones</v-toolbar-title>
        </v-toolbar>

        <v-divider />

        <v-card-text class="pb-1 background">
          <v-sheet v-for="s in instance.sources" class="mb-4" :key="`source-${s.id}`" outlined rounded>
            <v-row>
              <!-- payments with source of reusable use and none flow -->
              <template v-if="s.usage === 'reusable'">
                <v-col cols="5">
                  <v-list>
                    <v-list-item>
                      <v-list-item-title>Monto</v-list-item-title>
                      <v-list-item-subtitle>{{instance.amount | currency(instance.currency)}} {{instance.currency}}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Método de pago</v-list-item-title>
                      <v-list-item-subtitle>Cargo a la tarjeta</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Número de tarjeta</v-list-item-title>
                      <v-list-item-subtitle class="font-weight-medium subtitle-1"><code class="blue-grey lighten-4 text--primary">•••• •••• •••• {{s.card.last4}}</code></v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Marca</v-list-item-title>
                      <v-list-item-subtitle>
                        <img :src="require(`@/assets/appAsset-brand--${s.card.brand}.svg`)" :alt="s.gateway" height="24">
                      </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Procesado el</v-list-item-title>
                      <v-list-item-subtitle>{{instance.intent.created | dateTime}}</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="5">
                  <v-list>
                    <v-list-item>
                      <v-list-item-title>ID transacción</v-list-item-title>
                      <v-list-item-subtitle>{{instance.intent.gateway_id}}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Funding</v-list-item-title>
                      <v-list-item-subtitle v-if="instance.intent && instance.intent.funding">
                        <template>
                          <template v-if="instance.intent.funding === 'credit'">Crédito</template>
                          <template v-else-if="instance.intent.funding === 'debit'">Débito</template>
                          <template v-else-if="instance.intent.funding === 'prepaid'">Prepago</template>
                          <template v-else>{{instance.intent.funding}}</template>
                        </template>
                      </v-list-item-subtitle>
                      <v-list-item-subtitle class="font-italic text--disabled" v-else>No especificado por el Gateway</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Procesado a través de</v-list-item-title>
                      <v-list-item-subtitle>
                        <img class="mt-1" :src="require(`@/assets/appAsset-brand--${s.gateway}.svg`)" height="22" />
                      </v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="2" class="text-right">
                  <template v-if="instance.status === 'succeeded' && instance.source.usage === 'reusable' && !instance.refunds.length">
                    <v-btn @click="dialogRefund=true" class="mr-3" color="secondary" retain-focus-on-click outlined :ripple="false"><v-icon left size="20">mdi-reply</v-icon>Reembolsar</v-btn>
                  </template>
                </v-col>
              </template>
              <!-- end payments with source of reusable use and none flow -->

              <!-- payments with source of single use and redirect flow -->
              <template v-else-if="s.usage === 'single' && ['redirect', 'none'].includes(s.flow)">
                <v-col cols="5">
                  <v-list>
                    <v-list-item>
                      <v-list-item-title>Monto</v-list-item-title>
                      <v-list-item-subtitle>{{s.amount | currency(instance.currency)}} {{instance.currency}}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Método de pago</v-list-item-title>
                      <v-list-item-subtitle>Tarjeta</v-list-item-subtitle>
                    </v-list-item>
                    <template v-if="s.card">
                      <v-list-item v-if="s.card.brand">
                        <v-list-item-title>Marca de la tarjeta</v-list-item-title>
                        <v-list-item-subtitle>
                          <img :src="require(`@/assets/appAsset-brand--${s.card.brand}.svg`)" :alt="s.gateway" height="24">
                        </v-list-item-subtitle>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-title>Número</v-list-item-title>
                        <v-list-item-subtitle class="font-weight-medium subtitle-1"><code class="blue-grey lighten-4 blue-grey--text darken-4--text">•••• •••• •••• {{s.card.last4}}</code></v-list-item-subtitle>
                      </v-list-item>
                    </template>
                    <v-list-item>
                      <v-list-item-title>Procesado el</v-list-item-title>
                      <v-list-item-subtitle>{{instance.payment_date | dateTime}}</v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="5">
                  <v-list>
                    <v-list-item>
                      <v-list-item-title>ID transacción</v-list-item-title>
                      <v-list-item-subtitle>{{instance.intent.gateway_id}}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Funding</v-list-item-title>
                      <v-list-item-subtitle v-if="instance.intent && instance.intent.funding">
                        <template>
                          <template v-if="instance.intent.funding === 'credit'">Crédito</template>
                          <template v-else-if="instance.intent.funding === 'debit'">Débito</template>
                          <template v-else-if="instance.intent.funding === 'prepaid'">Prepago</template>
                          <template v-else>{{instance.intent.funding}}</template>
                        </template>
                      </v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-title>Procesado a través de</v-list-item-title>
                      <v-list-item-subtitle>
                        <img class="mt-1" :src="require(`@/assets/appAsset-brand--${s.gateway}.svg`)" :alt="s.gateway" height="22">
                      </v-list-item-subtitle>
                    </v-list-item>
                  </v-list>
                </v-col>
              </template>
              <!-- end payments with source of single use and redirect flow -->

              <!-- payments with source of single use and receiver flow -->
              <template v-if="s.usage === 'single' && s.flow === 'receiver'">
                <template v-if="s.gateway === 'offline_payment'">
                  <v-col cols="5">
                    <v-list>
                      <v-list-item>
                        <v-list-item-title>Monto</v-list-item-title>
                        <v-list-item-subtitle>{{s.amount | currency(instance.currency)}} {{instance.currency}}</v-list-item-subtitle>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-title>Método de pago</v-list-item-title>
                        <v-list-item-subtitle>Offline</v-list-item-subtitle>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-title>Tipo</v-list-item-title>
                        <v-list-item-subtitle v-if="s.offline_payment.method === 'bank_transfer'">Transferencia bancaria</v-list-item-subtitle>
                        <v-list-item-subtitle v-else-if="s.offline_payment.method === 'cash'">Efectivo</v-list-item-subtitle>
                        <v-list-item-subtitle v-else-if="s.offline_payment.method === 'check'">Cheque</v-list-item-subtitle>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-title>Creado el</v-list-item-title>
                        <v-list-item-subtitle>{{s.offline_payment.created | dateTime}}</v-list-item-subtitle>
                      </v-list-item>
                    </v-list>
                  </v-col>
                  <v-col cols="5">
                    <v-list>
                      <v-list-item>
                        <v-list-item-title>Fecha de la transacción</v-list-item-title>
                        <v-list-item-subtitle>{{instance.payment_date | date}}</v-list-item-subtitle>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-title>Nota interna</v-list-item-title>
                        <v-list-item-subtitle>{{s.description}}</v-list-item-subtitle>
                      </v-list-item>
                    </v-list>
                  </v-col>
                  <v-col cols="2" class="text-right">
                    <v-tooltip color="red" dark max-width="280" nudge-left="60" transition="scale-transition" top>
                      <template v-slot:activator="{on}">
                        <v-btn v-on="on" @click="destroySource(s.id)" class="mr-3" color="red" icon retain-focus-on-click outlined :ripple="false"><v-icon size="20">mdi-delete</v-icon></v-btn>
                      </template>
                      <span class="white--text">Eliminar transacción</span>
                    </v-tooltip>
                  </v-col>
                </template>
                <template v-else-if="s.gateway === 'ach_transfer'">
                  <v-col cols="5">
                    <v-list>
                      <v-list-item>
                        <v-list-item-title>Monto</v-list-item-title>
                        <v-list-item-subtitle>{{s.amount | currency(instance.currency)}} {{instance.currency}}</v-list-item-subtitle>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-title>Método de pago</v-list-item-title>
                        <v-list-item-subtitle>Transferencia</v-list-item-subtitle>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-title>Transferencia</v-list-item-title>
                        <v-list-item-subtitle>
                          <v-btn :to="{name: 'TransactionList', params: {id: s.ach_transfer}}" text color="primary" :ripple="false">{{s.ach_transfer}}<v-icon right>mdi-arrow-right</v-icon></v-btn>
                        </v-list-item-subtitle>
                      </v-list-item>
                      <v-list-item v-if="achTransfer.origin_holder_name">
                        <v-list-item-title>Nombre del titular</v-list-item-title>
                        <v-list-item-subtitle>{{achTransfer.origin_holder_name}}</v-list-item-subtitle>
                      </v-list-item>
                      <v-list-item v-if="achTransfer.status !== 'draft'">
                        <v-list-item-title>Creado el</v-list-item-title>
                        <v-list-item-subtitle>{{achTransfer.date | dateTime}}</v-list-item-subtitle>
                      </v-list-item>
                    </v-list>
                  </v-col>
                  <v-col cols="5">
                    <v-list>
                      <v-list-item v-if="achTransfer.origin_holder_id">
                        <v-list-item-title>ID del titular</v-list-item-title>
                        <v-list-item-subtitle>{{achTransfer.origin_holder_id}}</v-list-item-subtitle>
                      </v-list-item>
                      <v-list-item v-if="achTransfer.origin_number">
                        <v-list-item-title>ID Banco</v-list-item-title>
                        <v-list-item-subtitle>{{achTransfer.origin_number}}</v-list-item-subtitle>
                      </v-list-item>
                      <v-list-item>
                        <v-list-item-title>Descripción</v-list-item-title>
                        <v-list-item-subtitle>{{achTransfer.description}}</v-list-item-subtitle>
                      </v-list-item>
                    </v-list>
                  </v-col>
                  <v-col cols="2" class="text-right">
                    <v-tooltip color="red" dark max-width="280" nudge-left="60" transition="scale-transition" top>
                      <template v-slot:activator="{on}">
                        <v-btn v-on="on" @click="destroySource(s.id)" class="mr-3" color="red" icon retain-focus-on-click outlined :ripple="false"><v-icon size="20">mdi-delete</v-icon></v-btn>
                      </template>
                      <span class="white--text">Eliminar conciliación</span>
                    </v-tooltip>
                  </v-col>
                </template>
              </template>
              <!-- end payments with source of single use and receiver flow -->
            </v-row>
          </v-sheet>
        </v-card-text>
      </v-card>
      <!-- end pay -->

      <!-- BR boleto -->
      <v-card v-if="boletosList.length" class="pa-0 my-5">
        <v-toolbar flat height="55">
          <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Boleto bancario</v-toolbar-title>
          <v-spacer />
          <v-toolbar-items>
            <v-row align="center" justify="center" class="mr-1">
              <v-btn class="ml-1" color="secondary" :href="boletosList[0].pdf" target="_blank" outlined retain-focus-on-click :ripple="false">PDF<v-icon right small>mdi-open-in-new</v-icon></v-btn>
            </v-row>
          </v-toolbar-items>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0 background">
          <v-row align="center">
            <v-col cols="6">
              <v-list class="v-list-form-extended transparent">
                <v-list-item>
                  <v-list-item-title>ID</v-list-item-title>
                  <v-list-item-subtitle>{{ boletosList[0].id }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>ID Banco</v-list-item-title>
                  <v-list-item-subtitle>{{ boletosList[0].bill_id }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item>
                  <v-list-item-title>Creado el</v-list-item-title>
                  <v-list-item-subtitle>{{ boletosList[0].created | dateTime }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col class="text-center">
              <v-tooltip color="primary" top transition="scale-transition">
                <template v-slot:activator="{on}">
                  <v-card v-on="on" @click="toClipboard(boletosList[0].barcode)" class="mx-auto" color="transparent" elevation="0" max-width="340" :ripple="false" tile>
                    <img src="../../assets/appAsset-banks--boleto.svg" alt="boleto" >
                    <span class="d-block caption">{{ boletosList[0].barcode }}</span>
                  </v-card>
                </template>
                <span class="white--text">Copiar</span>
              </v-tooltip>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <!-- end BR boleto -->

      <!-- connections -->
      <v-card class="pa-0 my-5">
        <v-toolbar flat height="55">
          <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Conexiones</v-toolbar-title>
        </v-toolbar>
        <v-divider />
        <v-card-text class="pa-0 background">
          <v-row>
            <v-col cols="6" v-if="instance.invoice || instance.source">
              <v-list class="v-list-form transparent">
                <v-list-item v-if="instance.invoice">
                  <v-list-item-title>Factura</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-btn :to="{name:'InvoiceRetrieve', params:{id: instance.invoice}}" text color="primary" :ripple="false">{{instance.invoice}}<v-icon right>mdi-arrow-right</v-icon></v-btn>
                  </v-list-item-subtitle>
                </v-list-item>
                <v-list-item v-if="instance.source">
                  <v-list-item-title>Fuente de pago</v-list-item-title>
                  <v-list-item-subtitle>
                    <v-tooltip right transition="scale-transition">
                      <template v-slot:activator="{on}">
                        <v-btn class="font-weight-regular" @click="toClipboard(instance.source.id)" text color="secondary" v-on="on" :ripple="false">{{instance.source.id}}</v-btn>
                      </template>
                      <span>Copiar</span>
                    </v-tooltip>
                  </v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col v-else class="text-center py-5">
              <v-icon class="d-block" color="secondary" size="70">mdi-merge</v-icon>
              <span class="d-block subtitle-1">El pago no tiene conexiones</span>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
      <!-- end connections -->
    </v-col>

    <!-- dialog new refund -->
    <v-dialog v-model="dialogRefund" width="700" persistent no-click-animation scrollable>
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat height="55">
            <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Reembolsar pago</v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-row align="center" justify="center" class="mr-1">
                <v-btn class="ml-3" @click="dialogRefund=false" icon small retain-focus-on-click><v-icon size="24">mdi-close</v-icon></v-btn>
              </v-row>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0 background">
          <v-list class="v-list-form pt-6 transparent">
            <v-list-item class="py-2">
              <v-list-item-title>Monto</v-list-item-title>
              <v-list-item-subtitle>
                <v-col cols="5">
                  <v-text-field v-model.number="$v.refund.amount.$model" :error="$v.refund.amount.$error" required outlined dense single-line :suffix="instance.currency" hide-details background-color="white" />
                </v-col>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item class="py-2">
              <v-list-item-title>Razón del reembolso</v-list-item-title>
              <v-list-item-subtitle>
                <v-col cols="8">
                  <v-select v-model="refund.reason" :items="refundReasons" :error="$v.refund.reason.$error" outlined dense single-line item-text="label" item-value="value" hide-details background-color="white" />
                </v-col>
              </v-list-item-subtitle>
            </v-list-item>
            <v-list-item class="py-2">
              <v-list-item-title></v-list-item-title>
              <v-list-item-subtitle>
                <v-textarea v-model="$v.refund.note.$model" :error="$v.refund.note.$error" placeholder="Escribe detalles del reembolso..." outlined background-color="white" />
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn @click="dialogRefund=false" outlined>Cancelar</v-btn>
          <v-btn @click="createRefund" :loading="$store.state.payments.createLoader" color="primary">Reembolsar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- end dialog new refund -->

    <!-- dialog offline payment -->
    <v-dialog v-model="dialogSource" width="800" persistent no-click-animation scrollable>
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat height="55">
            <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Agregar pago externo</v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-row align="center" justify="center" class="mr-1">
                <v-btn class="ml-3" @click="dialogSource=false" icon small retain-focus-on-click><v-icon size="24">mdi-close</v-icon></v-btn>
              </v-row>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0 background">
          <v-row align="start" no-gutters>
            <v-col cols="7">
              <v-list class="py-3 v-list-form transparent">
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="ml-1 text--secondary subtitle-2 font-weight-medium">Monto</v-list-item-title>
                    <v-list-item-subtitle class="py-1">
                      <v-col cols="5">
                        <v-text-field v-model="$v.source.amount.$model" v-model-numeric :error="$v.source.amount.$error" background-color="white" dense hide-details outlined required single-line />
                      </v-col>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="ml-1 text--secondary subtitle-2 font-weight-medium">Medio de pago</v-list-item-title>
                    <v-list-item-subtitle class="py-1">
                      <v-col cols="7">
                        <v-select v-model="source.offlinePayment.method" :items="offlinePaymentMethods" item-text="label" item-value="value" single-line hide-details dense height="30" outlined background-color="white" />
                      </v-col>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="ml-1 text--secondary subtitle-2 font-weight-medium">Fecha de pago</v-list-item-title>
                    <v-list-item-subtitle class="py-1">
                      <v-col cols="5">
                        <v-menu v-model="datepicker" :close-on-content-click="false" offset-y transition="scale-transition">
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field v-model="$v.source.offlinePayment.date.$model" :error="$v.source.offlinePayment.date.$error" v-bind="attrs" v-on="on" background-color="white" outlined single-line dense readonly hide-details>
                              <template v-slot:append>
                                <v-icon class="mr-3" small color="secondary">mdi-calendar</v-icon>
                              </template>
                            </v-text-field>
                          </template>
                          <v-date-picker v-model="$v.source.offlinePayment.date.$model" no-title @input="datepicker=false" type="date" locale="es-cl" width="280" color="blue">
                            <div class="mx-auto">
                              <v-btn color="primary" @click="() => { $v.source.offlinePayment.date.$model = today; datepicker=false}">Hoy</v-btn>
                              <v-btn class="ml-2" outlined @click="() => {$v.source.offlinePayment.date.$model = ''; datepicker=false}">Limpiar</v-btn>
                            </div>
                          </v-date-picker>
                        </v-menu>
                      </v-col>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="ml-1 text--secondary subtitle-2 font-weight-medium">Nota interna</v-list-item-title>
                    <v-list-item-subtitle class="py-1">
                      <v-textarea v-model="$v.source.offlinePayment.description.$model" :error="$v.source.offlinePayment.description.$error" background-color="white" hide-details placeholder="Escribe los detalles del pago..." outlined />
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>

            <v-divider vertical />

            <v-col>
              <!-- balance summary -->
              <v-list class="pa-3 transparent">
                <v-list-item class="py-0">
                  <v-list-item-title class="text--primary">Pendiente</v-list-item-title>
                  <v-list-item-subtitle class="text-right text--secondary font-weight-medium">{{ instance.balance | currency(instance.currency) }} {{ instance.currency }}</v-list-item-subtitle>
                </v-list-item>
                <v-list-item class="py-0">
                  <v-list-item-title class="text--primary">Monto a pagar</v-list-item-title>
                  <v-list-item-subtitle class="text-right text--secondary font-weight-medium" :class="{ 'red--text text--darken-1': (source.amount > instance.balance) }">{{ source.amount | currency(instance.currency) }} {{ instance.currency }}</v-list-item-subtitle>
                </v-list-item>
                <v-divider class="my-1" />
                <v-list-item class="py-0">
                  <v-list-item-title class="text--primary">Nuevo pendiente</v-list-item-title>
                  <v-list-item-subtitle class="text-right text--secondary font-weight-medium" :class="{ 'red--text text--darken-1': (source.amount > instance.balance) }">{{ (instance.balance - source.amount) | currency(instance.currency) }} {{ instance.currency }}</v-list-item-subtitle>
                </v-list-item>
              </v-list>
              <!-- end balance summary -->

              <div class="mx-3 mb-5">
                <v-btn @click="createSource" :loading="$store.state.payments.createLoader" block color="primary" :ripple="false">Crear pago externo</v-btn>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- end dialog offline payment -->

    <!-- dialog authorization -->
    <v-dialog v-model="dialogAuthorization" width="600" persistent no-click-animation scrollable>
      <v-card>
        <v-card-title class="pa-0">
          <v-toolbar flat height="55">
            <v-toolbar-title class="subtitle-1 text--secondary font-weight-medium">Cargar a una tarjeta</v-toolbar-title>
            <v-spacer />
            <v-toolbar-items>
              <v-row align="center" justify="center" class="mr-1">
                <v-btn class="ml-3" @click="dialogAuthorization=false" icon small><v-icon size="24">mdi-close</v-icon></v-btn>
              </v-row>
            </v-toolbar-items>
          </v-toolbar>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-0 background">
          <template v-if="sourcesList.length">
            <v-radio-group class="px-7" v-model="authorization.source">
              <v-sheet class="overflow-hidden rounded my-2" v-for="s in sourcesList" :key="s.id" outlined>
                <v-radio class="px-3 py-2" :value="s.id">
                  <template v-slot:label>
                    <v-row class="pl-3" align="center" justify="space-between">
                      <v-col cols="2">
                        <img class="mt-2" :src="require(`@/assets/appAsset-brand--${s.card.brand}.svg`)" :alt="s.card.brand" height="24">
                      </v-col>
                      <v-col cols="5">
                        <span class="body-1 font-weight-medium">•••• •••• •••• {{s.card.last4}}</span>
                      </v-col>
                      <v-col class="text-right" cols="5">
                        <img class="mt-2" :src="require(`@/assets/appAsset-brand--${s.gateway}.svg`)" height="26px" />
                      </v-col>
                    </v-row>
                  </template>
                </v-radio>
              </v-sheet>
            </v-radio-group>
          </template>
          <v-row v-else>
            <v-col class="text-center py-5">
              <v-icon class="d-block" color="secondary" size="70">mdi-credit-card-remove</v-icon>
              <span class="d-block subtitle-1">El cliente no tiene tarjetas registradas</span>
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-4" v-if="sourcesList.length">
          <v-spacer />
          <v-btn @click="dialogAuthorization=false" outlined>Cancelar</v-btn>
          <v-btn @click="createAuthorization" :loading="loaderAuthorization" color="primary">Cargar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- end dialog authorization -->
  </v-row>
</template>
<script>
import { mapState } from 'vuex'
import { required, decimal, maxLength } from 'vuelidate/lib/validators'

import GenericViewMixin from '@/mixins/GenericViewMixin'

export default {
  mixins: [
    GenericViewMixin
  ],
  data: () => ({
    authorization: {
      source: ''
    },
    datepicker: false,
    dialogAuthorization: false,
    dialogRefund: false,
    dialogSource: false,
    editable: {
      enable_checkout: true
    },
    loaderAuthorization: false,
    offlinePaymentMethods: [
      { value: 'bank_transfer', label: 'Transferencia bancaria' },
      { value: 'cash', label: 'Efectivo' },
      { value: 'check', label: 'Cheque' }
    ],
    refund: {
      amount: null,
      reason: '',
      note: ''
    },
    refundReasons: [
      { value: 'duplicate', label: 'Duplicado' },
      { value: 'fraudulent', label: 'Fraudulento' },
      { value: 'requested_by_customer', label: 'Solicitado por el cliente' }
    ],
    source: {
      amount: null,
      offlinePayment: {
        date: null,
        description: '',
        method: 'bank_transfer'
      }
    }
  }),
  computed: {
    ...mapState({
      instance: state => state.payments.paymentsDetail,
      achTransfer: state => state.banks.ach_transfersDetail,
      boletosList: state => state.banks.boletosList,
      sourcesList: state => state.payments.sourcesList
    }),
    totalRefunded () {
      if (this.instance.refunds.length) {
        return this.sumProperty(this.instance.refunds, 'amount')
      } else {
        return 0
      }
    }
  },
  created () {
    this.$store.dispatch('payments/RETRIEVE', {
      resource: 'payments',
      id: this.$route.params.id
    })
    .then(() => {
      if ((((this.instance || {}).source || {}).usage) === 'single' && this.instance.source.flow === 'receiver') {
        this.$store.dispatch('banks/RETRIEVE', {
          resource: 'ach_transfers',
          id: this.instance.source.ach_transfer
        })
      }
    })
    .then(() => {
      if (this.instance.customer) {
        this.$store.dispatch('payments/LIST', {
          resource: 'sources',
          query: {
            customer: this.instance.customer.id,
            gateway__currencies__in: this.instance.currency,
            usage: 'reusable',
            status: 'chargeable'
          }
        })
        .then(() => {
          if (this.sourcesList.length) {
            this.authorization.source = this.sourcesList[0].id
          }
        })
      }
    })
    .then(() => {
      // retrieve boleto bancario if payment is from Brazil (BR)
      if (this.account.country.id === 'BR') {
        this.$store.dispatch('banks/LIST', {
          resource: 'boletos',
          query: {
            payment: this.$route.params.id
          }
        })
      }
    })
    .then(() => {
      this.editable.enable_checkout = this.instance.enable_checkout
    })
  },
  methods: {
    updatePayment () {
      this.$store.dispatch('payments/UPDATE', {
        resource: 'payments',
        id: this.$route.params.id,
        payload: this.editable
      })
      .then(() => {
        this.$dialog.message.info('Se ha actualizado el pago')
      })
      .catch((error) => {
        this.$dialog.message.error(this.parseErrors(error.response.data))
      })
    },
    destroyPayment () {
      this.$dialog.warning({
        title: 'Eliminar pago',
        text: `
          <span class="d-block mb-4">¿Estás seguro de continuar?</span>
          <span class="d-block font-weight-medium">Esta acción no se puede deshacer</span>
        `,
        actions: [
          {
            color: 'red',
            text: 'Continuar',
            handle: () => {
              this.$store.dispatch('payments/DESTROY', {
                resource: 'payments',
                id: this.instance.id
              })
              .then(() => {
                this.$router.push({
                  name: 'PaymentListCreate'
                })
              })
              .catch((error) => {
                this.$dialog.message.error(this.parseErrors(error.response.data))
              })
            }
          }
        ]
      })
    },
    createAuthorization () {
      this.$v.authorization.$touch()
      if (this.$v.authorization.$invalid) {
        return false
      }
      this.$dialog.warning({
        title: 'Cargar a una tarjeta',
        text: `
          <span class="d-block mb-4">Al continuar se cargarán <span class="font-weight-medium">
          ${this.$options.filters.currency(this.instance.amount, this.instance.currency)}
          ${this.instance.currency}</span> a la tarjeta seleccionada.</span>
          <span class="d-block mb-4">¿Estás seguro de continuar?</span>
          <span class="d-block font-weight-medium">Esta acción no se puede deshacer</span>
        `,
        actions: [
          {
            color: 'primary',
            text: 'Continuar',
            handle: () => {
              this.loaderAuthorization = true
              this.$store.dispatch('payments/CREATE', {
                resource: 'payments',
                id: this.instance.id,
                detail: 'authorize',
                payload: {
                  source: this.authorization.source
                },
                loader: false
              })
              .then(() => {
                this.$dialog.message.info('El pago ha sido cargado a la tarjeta')
              })
              .catch((error) => {
                this.$dialog.message.error(this.parseErrors(error.response.data))
              })
              .finally(() => {
                this.$store.dispatch('payments/RETRIEVE', {
                  resource: 'payments',
                  id: this.instance.id,
                  loader: false
                })
                this.loaderAuthorization = false
                this.dialogAuthorization = false
              })
            }
          }
        ]
      })
    },
    createRefund () {
      this.$v.refund.$touch()
      if (this.$v.refund.$invalid) {
        return false
      }
      this.$dialog.warning({
        title: 'Reembolsar pago',
        text: `
          <span class="d-block mb-4">¿Estás seguro de continuar?</span>
          <span class="d-block font-weight-medium">Esta acción no se puede deshacer</span>
        `,
        actions: [
          {
            color: 'red',
            text: 'Continuar',
            handle: () => {
             this.$store.dispatch('payments/CREATE', {
                resource: 'refunds',
                payload: {
                  payment: this.instance.id,
                  amount: this.refund.amount,
                  reason: this.refund.reason
                }
              })
              .then(() => {
                this.$store.dispatch('payments/RETRIEVE', {
                  resource: 'payments',
                  id: this.$route.params.id,
                  loader: false
                })
                this.$dialog.message.info('El pago ha sido reembolsado')
                this.dialogRefund = false
              })
              .catch((error) => {
                this.$dialog.message.error(this.parseErrors(error.response.data))
              })
            }
          }
        ]
      })
    },
    createSource () {
      this.$v.source.$touch()

      if (this.$v.source.$invalid) {
        return false
      }

      const payload = {
        amount: this.source.amount,
        description: this.source.offlinePayment.description,
        gateway: 'offline_payment',
        offline_payment: {
          currency: this.instance.currency,
          date: this.source.offlinePayment.date,
          method: this.source.offlinePayment.method
        },
        usage: 'single'
      }

      if (this.instance.customer) {
        payload.customer = this.instance.customer.id
      }

      this.$store.dispatch('payments/CREATE', {
        resource: 'sources',
        payload: payload
      })
      .then((response) => {
        this.$store.dispatch('payments/CREATE', {
          resource: 'payments',
          detail: 'authorize',
          id: this.$route.params.id,
          payload: {
            source: response.data.id
          },
          loader: false
        })
        .then(() => {
          this.dialogSource = false

          // reset fields & validations
          this.source.amount = null
          this.source.offlinePayment.date = null
          this.source.offlinePayment.description = ''
          this.source.offlinePayment.method = 'bank_transfer'
          this.$v.source.$reset()
        })
        .catch((error) => {
          this.$dialog.message.error(this.parseErrors(error.response.data))
        })
      })
    },
    createBoleto () {
      this.$dialog.warning({
        title: 'Generar boleto',
        text: `
          <span class="d-block mb-4">Una vez generado, podrás compartir el boleto para que el cliente realice el pago en cualquier banco o la aplicación PIX</span>
          <span class="d-block mb-4">¿Estás seguro de continuar?</span>
          <span class="d-block font-weight-medium">Esta acción no se puede deshacer</span>
        `,
        actions: [
          {
            color: 'primary',
            text: 'Continuar',
            handle: () => {
              this.$store.dispatch('banks/CREATE', {
                resource: 'boletos',
                payload: {
                  payment: this.instance.id
                }
              })
              .then(() => {
                this.$dialog.message.success('El boleto ha sido generado con éxito')
              })
              .catch((error) => {
                this.$dialog.message.error(this.parseErrors(error.response.data))
              })
            }
          }
        ]
      })
    },
    destroySource (id) {
      this.$dialog.warning({
        title: 'Eliminar transacción',
        text: `
          <span class="d-block mb-4">¿Estás seguro de continuar?</span>
          <span class="d-block font-weight-medium">Esta acción no se puede deshacer</span>
        `,
        actions: [
          {
            color: 'red',
            text: 'Continuar',
            handle: () => {
              this.$store.dispatch('payments/DESTROY', {
                resource: 'sources',
                id: id
              })
              .then(() => {
                this.$store.dispatch('payments/RETRIEVE', {
                  resource: 'payments',
                  id: this.$route.params.id,
                  loader: false
                })
              })
              .catch((error) => {
                this.$dialog.message.error(this.parseErrors(error.response.data))
              })
            }
          }
        ]
      })
    }
  },
  validations: {
    authorization: {
      source: {
        required
      }
    },
    refund: {
      amount: {
        required,
        decimal,
        maxValue: function (value) {
          if (this.instance.refunds.length) {
            return value <= (this.instance.amount - this.totalRefunded)
          } else {
            return this.instance.amount >= value
          }
        }
      },
      reason: {
        required
      },
      note: {
        maxLength: maxLength(64)
      }
    },
    source: {
      amount: {
        required,
        decimal,
        maxValue: function (value) {
          return value <= this.instance.balance
        }
      },
      offlinePayment: {
        date: {
          required
        },
        description: {
          required,
          maxLength: maxLength(256)
        },
        method: {
          required
        }
      }
    }
  }
}
</script>